import { useAnalytics } from '@ria-client/segment/Analytics';
import { useAnalyticsPageView } from '@ria-client/segment/Analytics/AnalyticsPageView';
import { useStandardTrack } from '@ria-client/segment/hooks/useStandardTrack';
import { useCallback, useRef } from 'react';

type PageActionProps = {
  actionDestinationUrl?: string;
  actionInput?: string | null;
  actionName: string;
  actionSection: string;
  actionType?: string;
  /** Confluence Link Viewed/Clicked — real link text when destination is set. */
  linkCopy?: string;
  additionalAttributes?: object;
  contextCampaignUtmCampaign?: string;
  /** Required: event_id from paired Element/Link Viewed (FeatureViewed / useNWAElementImpression). */
  parentEventId: string;
};

type PageActionOverride = {
  destination_url?: string;
  entity_name?: string;
  page_url?: string;
  parent_event_id?: string;
  link_copy?: string;
  additional_attributes?: Record<string, unknown>;
  action_input?: string | null;
  action_section?: string;
  action_type?: string;
  event_id?: string;
  [key: string]: unknown;
};

export const usePageAction = ({
  actionDestinationUrl,
  actionInput = null,
  actionName,
  actionSection,
  actionType,
  linkCopy,
  additionalAttributes,
  contextCampaignUtmCampaign,
  parentEventId: hookParentEventId,
}: PageActionProps) => {
  const track = useStandardTrack();
  const { deviceId } = useAnalytics();
  const { pageType } = useAnalyticsPageView();

  // Hook props live in a ref so the returned fire function keeps a stable identity —
  // callers pass it straight into useCallback/useEffect dependency arrays.
  const propsRef = useRef<PageActionProps>({
    actionDestinationUrl,
    actionInput,
    actionName,
    actionSection,
    actionType,
    linkCopy,
    additionalAttributes,
    contextCampaignUtmCampaign,
    parentEventId: hookParentEventId,
  });
  propsRef.current = {
    actionDestinationUrl,
    actionInput,
    actionName,
    actionSection,
    actionType,
    linkCopy,
    additionalAttributes,
    contextCampaignUtmCampaign,
    parentEventId: hookParentEventId,
  };

  return useCallback(
    (overrideProps?: PageActionOverride | null, callback?: (() => void) | null) => {
      const {
        actionDestinationUrl,
        actionInput,
        actionName,
        actionSection,
        actionType,
        linkCopy,
        additionalAttributes,
        contextCampaignUtmCampaign,
        parentEventId: hookParentEventId,
      } = propsRef.current;

      const {
        destination_url: overrideDestinationUrl,
        entity_name: overrideEntityName,
        page_url: pageUrl,
        parent_event_id: overrideParentEventId,
        link_copy: overrideLinkCopy,
        additional_attributes: overrideAdditional = {},
        action_input: overrideActionInput,
        action_section: overrideActionSection,
        action_type: overrideActionType,
        event_id: overrideEventId,
        ...restOverrides
      } = overrideProps ?? {};

      const destinationUrl = overrideDestinationUrl ?? actionDestinationUrl;
      const entityName = overrideEntityName ?? actionName;
      const resolvedLinkCopy = overrideLinkCopy ?? linkCopy;
      const resolvedActionType = overrideActionType ?? actionType;
      const resolvedActionSection = overrideActionSection ?? actionSection;
      const parentEventId = overrideParentEventId ?? hookParentEventId;

      // No paired view event means no valid Element/Link payload — still run the caller's
      // callback so analytics never blocks navigation or UI side effects.
      if (!parentEventId) {
        callback?.();
        return;
      }

      const legacyAdditional = {
        page_type: pageType,
        action_input: overrideActionInput !== undefined ? overrideActionInput : actionInput,
        action_name: entityName,
        action_section: resolvedActionSection,
        ...(resolvedActionType ? { action_type: resolvedActionType } : {}),
        ...(contextCampaignUtmCampaign
          ? { context_campaign_utm_campaign: contextCampaignUtmCampaign }
          : {}),
        ...(deviceId ? { traditional_device_id: deviceId } : {}),
        ...(overrideEventId ? { event_id: overrideEventId } : {}),
        ...restOverrides,
        ...additionalAttributes,
        ...overrideAdditional,
      };

      // Link Clicked requires link_copy; without it fall through to Element Interacted
      // (with the destination preserved) rather than dropping the interaction.
      if (destinationUrl && resolvedLinkCopy) {
        return void track('Link Clicked', {
          entity_name: entityName,
          destination_url: destinationUrl,
          parent_event_id: parentEventId,
          link_copy: resolvedLinkCopy,
          ...(pageUrl ? { page_url: pageUrl } : {}),
          additional_attributes: legacyAdditional,
        })?.finally(() => {
          callback?.();
        });
      }

      return void track('Element Interacted', {
        entity_name: entityName,
        parent_event_id: parentEventId,
        ...(pageUrl ? { page_url: pageUrl } : {}),
        additional_attributes: {
          ...legacyAdditional,
          ...(destinationUrl ? { destination_url: destinationUrl } : {}),
        },
      })?.finally(() => {
        callback?.();
      });
    },
    [track, deviceId, pageType],
  );
};
